import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { isMemoEntityType } from '@/lib/crm/record/notes/notes.utils';

import type { CardRow, PipelineCardConfig } from './cardConfig';
import { selectInlineFields } from './cardConfig';

export function buildDefaultCardConfig(
  entityType: string,
  propertySchemas: Record<string, PropertyTypeSchema>,
  associationTypeMap: Map<string, string>
): PipelineCardConfig {
  if (entityType === 'ahoy_deal') {
    return {
      rows: [
        {
          slots: [
            {
              type: 'association',
              name: 'ahoy_deal_to_company',
              fallbacks: [
                { type: 'association', name: 'ahoy_deal_to_contact' },
              ],
            },
          ],
        },
        {
          slots: [
            { type: 'property', name: 'ahoy_amount' },
            { type: 'property', name: 'ahoy_close_date' },
          ],
        },
      ],
    };
  }

  // Notes and tasks share the memo contract (title + markdown body): the card
  // body is the content excerpt. Without this a task falls through to the
  // generic default, whose inline fields repeat the title under the title.
  if (isMemoEntityType(entityType)) {
    return {
      rows: [{ slots: [{ type: 'property', name: 'ahoy_content' }] }],
    };
  }

  const rows: CardRow[] = [];

  const hasCompany = associationTypeMap.has(`${entityType}_to_company`);
  const hasContact = associationTypeMap.has(`${entityType}_to_contact`);

  if (hasCompany) {
    rows.push({
      slots: [
        {
          type: 'association',
          name: `${entityType}_to_company`,
          ...(hasContact
            ? {
                fallbacks: [
                  { type: 'association', name: `${entityType}_to_contact` },
                ],
              }
            : {}),
        },
      ],
    });
  } else if (hasContact) {
    rows.push({
      slots: [{ type: 'association', name: `${entityType}_to_contact` }],
    });
  }

  for (const schema of selectInlineFields(propertySchemas)) {
    rows.push({
      slots: [{ type: 'property', name: schema.property_type_name }],
    });
  }

  return { rows };
}
